import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, generatePath, Link } from 'react-router-dom'
import { ROUTES } from '../../routes'
import QUERY_KEYS from '../../graphql/queryKeys'
import notify from '../../utilities/notify'
import { TICKET_BILLING_STATUSES } from '../../utilities/constants'
import styles from './index.module.scss'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import PageTitle from '../../components/page-title'
import { Spinner } from '../shared/Spinner'
import FormExplanation from '../shared/FormExplanation'
import NewTicketForm from '../../components/create-ticket/NewTicketForm'
import RecentTickets from '../../components/RecentTickets'
import useQuery from '../../hooks/useQuery'
import useMutation from '../../hooks/useMutation'
import createTicketPageQuery from '../../graphql/queries/pages/createTicketPage'
import createNewTicketMutation from '../../graphql/mutations/createNewTicket'
import createRecurringTicketsMutation from '../../graphql/mutations/createRecurringTickets'
import feeTypesQuery from '../../graphql/queries/feeTypes'
import { captureErrorAndNotify, handleValidateAndNotify } from '../../utilities/errorHandlers'
import { TERMS_AND_CONDITION_WARNING_KIND } from '../../utilities/termsAndConditionWarnings'
import useTermsAndConditionExpiredWarning from '../../components/terms-and-condition-expired-warning-modal/useTermsAndConditionExpiredWarning'
import usersQuery from '../../graphql/queries/users'

export default function CreateTicketPage () {
  const [ticketTypeBillingStatusSelected, setTicketTypeBillingStatusSelected] = useState(null)
  const [resourceTypeBillingStatusSelected, setResourceTypeBillingStatusSelected] = useState(null)
  const history = useHistory()
  const location = useLocation()
  const queryParams = useMemo(() => {
    return new URLSearchParams(location.search)
  }, [location.search])
  const jobId = queryParams.get('job_id')
  const serviceFrequency = queryParams.get('serviceFrequency')
  const { hauler, user } = useSelector(({ user: { hauler, user } }) => ({ hauler, user }))
  const isRecurring = hauler.isRecurringServiceEnabled && serviceFrequency === 'RECURRING'

  const { data: pageData, isFetching: isFetchingPageData } = useQuery(
    [QUERY_KEYS.createTicketPage, jobId, user.haulerId],
    createTicketPageQuery
  )

  const { mutate: createTicket, isLoading: isCreatingTicket } = useMutation(createNewTicketMutation, {
    onSuccess ({ ticket }, variables) {
      notify('success', 'Ticket Successfully Created!')
      const navigateTo = variables.navigateToFees
        ? generatePath(ROUTES.ticket, { id: ticket.id })
        : generatePath(ROUTES.job, { id: jobId })
      history.push({
        pathname: navigateTo,
        search: variables.navigateToFees ? `?${new URLSearchParams({ scrollTo: 'fees' }).toString()}` : undefined
      })
    },
    onError (error) {
      handleValidateAndNotify(error, 'Unable to update ticket')
    }
  })

  const { mutate: createRecurringTickets, isLoading: isCreatingRecurringTickets } = useMutation(createRecurringTicketsMutation, {
    onSuccess () {
      notify('success', 'Recurring Tickets creation started!')
      const navigateTo = generatePath(ROUTES.job, { id: jobId })

      history.push({
        pathname: navigateTo
      })
    },
    onError (error) {
      handleValidateAndNotify(error, 'Unable to create recurring tickets')
    }
  })

  const { data: feeTypesData } = useQuery(
    [QUERY_KEYS.feeTypes, 'ENABLED'],
    feeTypesQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Error fetching fee types')
      },
      placeholderData: { feeTypes: {} }
    }
  )

  const { data: { users } = {} } = useQuery([
    QUERY_KEYS.users,
    user.haulerId,
    true,
    null,
    null,
    hauler.showDriversOnly ? 'hauler_driver' : null
  ],
  usersQuery,
  {
    onError (error) {
      captureErrorAndNotify(error, 'Unable to retrieve user list')
    },
    placeholderData: { users: { nodes: [] } }
  })

  const { warnOrContinue, warningModal } = useTermsAndConditionExpiredWarning(
    pageData?.job?.client,
    TERMS_AND_CONDITION_WARNING_KIND.TICKET
  )

  function handleFormSubmit (vals) {
    warnOrContinue(() => {
      const { navigateToFees, ...formValues } = vals
      return createTicket({ ticketParams: { ...formValues, jobId }, navigateToFees })
    })
  }

  function handleRecurringTicketFormSubmit (vals) {
    warnOrContinue(() => {
      const { ...formValues } = vals
      createRecurringTickets({ ticketParams: { ...formValues, jobId } })
    })
  }

  const initialAssetValues = {
    serviceInterval: null,
    serviceDay: [],
    serviceDate: [],
    serviceDailyFrequency: null,
    startDate: null,
    endDate: null,
    ticketFees: []
  }

  const isLoading = isFetchingPageData || isCreatingTicket

  return (
    <NormalLayoutContainer>
      {warningModal}
      <PageTitle>
        <div className={styles.pageTitle}>
          <div>{`${(isRecurring) ? 'Create Recurring Tickets' : 'Create a New Ticket'}`}</div>
          <Link className='dis-btn dis-btn-blank dis-btn-lg' to={generatePath(ROUTES.job, { id: jobId })}>
            Cancel
          </Link>
        </div>
      </PageTitle>

      {
        isLoading || isCreatingRecurringTickets
          ? <Spinner isFetching />
          : (<>
            <div className='dis-form-page-container'>
              <div className='dis-form-container-left'>
                <div className='dis-panel'>
                  <div className='dis-panel-body'>
                    <NewTicketForm
                      onSubmit={isRecurring ? handleRecurringTicketFormSubmit : handleFormSubmit}
                      resourceTypes={pageData.resourceTypes.map((item) => ({ ...item, value: item.id, label: item.name }))}
                      ticketTypes={pageData.ticketTypes}
                      billingStatuses={TICKET_BILLING_STATUSES}
                      ticketTypeBillingStatusSelected={ticketTypeBillingStatusSelected}
                      setTicketTypeBillingStatusSelected={setTicketTypeBillingStatusSelected}
                      resourceTypeBillingStatusSelected={resourceTypeBillingStatusSelected}
                      setResourceTypeBillingStatusSelected={setResourceTypeBillingStatusSelected}
                      serviceFrequency={serviceFrequency}
                      feeTypes={feeTypesData.feeTypes}
                      users={users?.nodes}
                      initialAssetValues={initialAssetValues}
                    />
                  </div>
                </div>
              </div>
              <div className='dis-form-container-right'>
                <FormExplanation
                  messages={[
                    'Tickets are the action that takes place on jobs. Here you will select the following:',
                    ' - Date in which the action needs to take place',
                    ' - Type of action needed',
                    " - Asset type that's needed",
                    ' - Add comments to each specific action (if needed)'
                  ]}
                />
              </div>
            </div>

            <div className={styles.recentTickets}>
              {pageData.ticketsSearch.nodes && <RecentTickets tickets={pageData.ticketsSearch.nodes} job={pageData.job} />}
            </div>
          </>)
      }

    </NormalLayoutContainer>
  )
}
